import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, ShoppingBag, HelpCircle, Facebook } from 'lucide-react';

/**
 * Floating "Need help?" button — the single biggest conversion saver.
 *
 * Real buyers who can't figure out "how to order?" abandon silently. Most of
 * them WILL message us if the option is one tap away. This widget puts a
 * persistent Messenger-style entry point on every public page with three
 * one-tap escape hatches:
 *
 *   1. "How to Order" — opens a quick visual mini-guide right in the panel
 *      (no navigation away = no extra friction).
 *   2. "Shop Now" — direct deep-link, in case they were stuck on the wrong page.
 *   3. "Message us on Facebook" — opens m.me chat in a new tab. Owner replies
 *      manually, but the buyer feels held.
 *
 * Auto-pops the panel once per session after the user has been idle on the
 * homepage for 25s. Only fires for fresh visitors (no localStorage flag yet),
 * and is dismissible. Survival logic: kill the timer the moment the user
 * interacts so we don't interrupt active scrollers.
 *
 * Hidden on admin / checkout / cart / login pages so it never gets in the way
 * of users who are clearly ALREADY converting.
 */

const HIDE_ON_PATHS = ['/admin', '/checkout', '/cart', '/login', '/register', '/admin-login'];
const FB_MESSENGER_URL = 'https://m.me/61588041885547';
const FB_PAGE_URL = 'https://www.facebook.com/profile.php?id=61588041885547';

export default function FloatingHelpButton() {
  const [open, setOpen] = useState(false);
  const [autoShown, setAutoShown] = useState(false);
  const location = useLocation();

  const shouldHide = HIDE_ON_PATHS.some(p => location.pathname.startsWith(p));

  // Auto-pop after 25s of homepage idle, once per session
  useEffect(() => {
    if (shouldHide) return;
    if (location.pathname !== '/') return;
    try {
      if (sessionStorage.getItem('jsf_help_autoshown') === '1') return;
    } catch { /* sessionStorage might be unavailable */ }

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setOpen(true);
      setAutoShown(true);
      try { sessionStorage.setItem('jsf_help_autoshown', '1'); } catch { /* noop */ }
    }, 25_000);

    const dismissAuto = () => clearTimeout(timer);
    window.addEventListener('scroll', dismissAuto, { passive: true, once: true });
    window.addEventListener('click', dismissAuto, { once: true });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', dismissAuto);
      window.removeEventListener('click', dismissAuto);
    };
  }, [location.pathname, shouldHide]);

  if (shouldHide) return null;

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          open ? 'bg-white/10 backdrop-blur-md border border-white/20' : 'bg-[#D4AF37] hover:scale-110'
        }`}
        aria-label="Need help?"
        data-testid="floating-help-btn"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <MessageCircle className="w-6 h-6 text-black" />}
        {!open && !autoShown && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-[#0A0A0A] animate-pulse" />
        )}
      </button>

      {/* Help panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-40 w-[calc(100vw-2.5rem)] max-w-[340px] bg-[#0F0F0F] border border-white/10 shadow-2xl animate-fade-in-up"
          data-testid="floating-help-panel"
        >
          <div className="p-4 border-b border-white/5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans mb-1">Need help?</p>
            <h3 className="text-white text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              We're here to assist
            </h3>
            <p className="text-white/50 text-xs mt-1 font-sans">Choose how we can help you order:</p>
          </div>

          <div className="p-2">
            {/* How to order quick guide */}
            <details className="group" data-testid="help-how-to-order">
              <summary className="list-none cursor-pointer p-3 hover:bg-white/[0.03] flex items-center gap-3 transition-colors">
                <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-sans font-medium">How to order</p>
                  <p className="text-white/40 text-[11px] font-sans">Quick step-by-step guide</p>
                </div>
                <span className="text-white/40 text-xs group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <ol className="px-3 pb-3 pt-1 space-y-2 text-xs font-sans text-white/70">
                <li className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">1.</span>
                  <span>Browse <Link to="/shop" onClick={() => setOpen(false)} className="text-[#D4AF37] underline">our shop</Link> and tap a fragrance</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">2.</span>
                  <span>Choose size → tap <strong className="text-white">Add to Cart</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">3.</span>
                  <span>Tap the cart icon (top-right) → <strong className="text-white">Checkout</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">4.</span>
                  <span>Fill in shipping → pick <strong className="text-white">COD</strong> or pay online via GCash</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">5.</span>
                  <span>We ship within 24 hours · Track via SMS</span>
                </li>
              </ol>
            </details>

            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 p-3 hover:bg-white/[0.03] transition-colors"
              data-testid="help-shop-now"
            >
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-sans font-medium">Browse the shop</p>
                <p className="text-white/40 text-[11px] font-sans">See all fragrances</p>
              </div>
            </Link>

            <a
              href={FB_MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 p-3 hover:bg-white/[0.03] transition-colors"
              data-testid="help-messenger"
            >
              <div className="w-9 h-9 rounded-full bg-[#1877F2]/15 border border-[#1877F2]/40 flex items-center justify-center flex-shrink-0">
                <Facebook className="w-4 h-4 text-[#1877F2]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-sans font-medium">Message us on Messenger</p>
                <p className="text-white/40 text-[11px] font-sans">Usually replies in minutes</p>
              </div>
            </a>

            <a
              href={FB_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 p-3 hover:bg-white/[0.03] transition-colors"
              data-testid="help-fb-page"
            >
              <div className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center flex-shrink-0">
                <Facebook className="w-4 h-4 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-sans font-medium">Visit our Facebook page</p>
                <p className="text-white/40 text-[11px] font-sans">Reviews · Photos · Updates</p>
              </div>
            </a>
          </div>

          <p className="text-center text-white/30 text-[10px] font-sans py-2 border-t border-white/5">
            JS Fragrance · Authentic luxury, accessible price
          </p>
        </div>
      )}
    </>
  );
}
